import { formatFileSize, isDuplicateFile } from "./attachment-manager";

// listen to file changes
// - on upload, perform:
//    - update in-memory list
//    - update dom
//    - validation internally
//    - update errors

// parse configuration
// parse project program area id
// parse attachment types
// events:
//   - upload
//   - removal
//   - program area update
//     - redraw options

// Ideas for refactoring:
// - Filelist manager is only validating
// - duplicate list of files (one in errors and one in dropdown manager)
//    - errors (file list manager)
//    - input synchronization (dropdown manager)

export interface FileInfo {
  fileCount: number;
  totalSize: number;
  existingFiles: ExistingFileInfo[];
  currentFiles: FileWithSelection[];
}

export interface FileWithSelection {
  file: File;
  currentAttachmentTypeId?: string;
}

export interface ExistingFileInfo {
  id: string;
  name: string;
  size: number;
  currentAttachmentTypeId?: string;
}

// Dedicated file management class
export class FilelistManager {
  private currentFiles: FileWithSelection[] = [];
  private existingFiles: ExistingFileInfo[] = [];
  private validationConfig: ValidationConfig;

  constructor(
    existingFiles: ExistingFileInfo[],
    validationConfig: ValidationConfig,
  ) {
    this.existingFiles = existingFiles;
    this.validationConfig = validationConfig;
  }

  public addFiles(files: File[]): string[] {
    const errors: string[] = [];

    for (const file of files) {
      // Check file count limit first
      const countValidation = validateFileCount(
        this.currentFiles.length + this.existingFiles.length,
        this.validationConfig.maxFileCount,
      );
      if (!countValidation.isValid) {
        errors.push(...countValidation.errors);
        break;
      }

      // Validate the file
      // TODO: confusingly separate
      const validation = validateFile(
        file,
        this.currentFiles.map((f) => f.file),
        this.validationConfig,
      );

      if (validation.isValid) {
        this.currentFiles.push({ file, currentAttachmentTypeId: undefined });
      } else if (validation.errors.length > 0) {
        errors.push(...validation.errors);
      }
      // If no errors but not valid, it's a duplicate - skip silently
    }

    return errors;
  }

  public clearExistingAttachmentTypes(): void {
    this.existingFiles.forEach((f) => {
      f.currentAttachmentTypeId = undefined;
    });
  }

  public removeFile(index: number): void {
    this.currentFiles.splice(index, 1);
  }

  public removeExistingFile(fileId: string): void {
    this.existingFiles = this.existingFiles.filter(
      (file) => file.id !== fileId,
    );
  }

  public getFileInfo(): FileInfo {
    return {
      totalSize: this.getTotalSize(),
      fileCount: this.getFileCount(),
      existingFiles: this.getExistingFiles(),
      currentFiles: [...this.currentFiles],
    };
  }

  public getCurrentFiles(): File[] {
    return this.currentFiles.map((f) => f.file);
  }

  public getExistingFiles(): ExistingFileInfo[] {
    return [...this.existingFiles];
  }

  public getAllFiles(): (FileWithSelection | ExistingFileInfo)[] {
    return [...this.existingFiles, ...this.currentFiles];
  }

  public getFileCount(): number {
    return this.existingFiles.length + this.currentFiles.length;
  }

  public getTotalSize(): number {
    const existingSize = this.existingFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );
    const currentSize = this.currentFiles.reduce(
      (sum, fileWithSelection) => sum + fileWithSelection.file.size,
      0,
    );
    return existingSize + currentSize;
  }

  public getExistingFileIds(): string[] {
    return this.existingFiles.map((f) => f.id);
  }

  public updateNewFileAttachmentType(
    index: number,
    attachmentTypeId: string,
  ): void {
    if (index >= 0 && index < this.currentFiles.length) {
      this.currentFiles[index].currentAttachmentTypeId = attachmentTypeId;
    }
  }

  public updateExistingFileAttachmentType(
    fileId: string,
    attachmentTypeId: string,
  ): void {
    const existingFile = this.existingFiles.find((f) => f.id === fileId);
    if (existingFile) {
      existingFile.currentAttachmentTypeId = attachmentTypeId;
    }
  }
}

function validateFileSize(file: File, maxSizeBytes: number): ValidationResult {
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      errors: [
        `${file.name} is too large (max ${formatFileSize(maxSizeBytes)})`,
      ],
    };
  }
  return { isValid: true, errors: [] };
}

function validateFileType(
  file: File,
  acceptedTypes: string[],
): ValidationResult {
  if (acceptedTypes.length === 0) {
    return { isValid: true, errors: [] };
  }

  const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
  const isAccepted = acceptedTypes.some(
    (type) => type.toLowerCase() === fileExtension,
  );

  if (!isAccepted) {
    return {
      isValid: false,
      errors: [`${file.name} is not an accepted file type`],
    };
  }

  return { isValid: true, errors: [] };
}

function validateFileCount(
  currentCount: number,
  maxCount: number,
): ValidationResult {
  if (currentCount >= maxCount) {
    return {
      isValid: false,
      errors: [`Maximum ${maxCount} files allowed`],
    };
  }
  return { isValid: true, errors: [] };
}

function validateFile(
  file: File,
  existingFiles: File[],
  config: ValidationConfig,
): ValidationResult {
  const errors: string[] = [];

  // Check for duplicates
  if (isDuplicateFile(file, existingFiles)) {
    return { isValid: false, errors: [] }; // Skip silently for duplicates
  }

  // Validate file size
  const sizeValidation = validateFileSize(file, config.maxFileSizeBytes);
  if (!sizeValidation.isValid) {
    errors.push(...sizeValidation.errors);
  }

  // Validate file type
  const typeValidation = validateFileType(file, config.acceptedTypes);
  if (!typeValidation.isValid) {
    errors.push(...typeValidation.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// File validation utility functions
interface ValidationConfig {
  maxFileCount: number;
  maxFileSizeBytes: number;
  acceptedTypes: string[];
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}
